import JSZip from "jszip";

const DOCUMENT_PATH = "word/document.xml";
const RELS_PATH = "word/_rels/document.xml.rels";
const HEADER_FOOTER_REL_TYPE = /\/(header|footer)$/;
const XML_DECLARATION = /^\s*<\?xml[^?]*\?>/;

const IMPORTED_PART_PATTERN = /^word\/(document|header\d*|footer\d*|footnotes|endnotes)\.xml$/i;

const EMPTY_HYPERLINK = /<w:hyperlink\b[^>]*(?:\/>|>\s*<\/w:hyperlink>)/g;

const removeEmptyHyperlinks = (xmlString) => xmlString.replace(EMPTY_HYPERLINK, "");

const getFirstSectionRefIds = (documentXml) => {
    const doc = new DOMParser().parseFromString(documentXml, "application/xml");
    if (doc.getElementsByTagName("parsererror").length) return [];

    const sectPr = doc.getElementsByTagName("w:sectPr")[0];
    if (!sectPr) return [];

    return Array.from(sectPr.children)
        .filter((el) => el.tagName === "w:headerReference" || el.tagName === "w:footerReference")
        .map((el) => el.getAttribute("r:id"))
        .filter(Boolean);
};

const moveRelationshipsToFront = (relsXml, rIds) => {
    if (rIds.length === 0) return relsXml;

    const doc = new DOMParser().parseFromString(relsXml, "application/xml");
    if (doc.getElementsByTagName("parsererror").length) return relsXml;

    const root = doc.getElementsByTagName("Relationships")[0];
    if (!root) return relsXml;

    const headerFooterRels = Array.from(root.children).filter(
        (el) => el.tagName === "Relationship" && HEADER_FOOTER_REL_TYPE.test(el.getAttribute("Type") || "")
    );
    const wanted = headerFooterRels.filter((el) => rIds.includes(el.getAttribute("Id")));
    if (wanted.length === 0) return relsXml;

    // Already ahead of every other header/footer relationship - nothing to do.
    const lastWantedIndex = Math.max(...wanted.map((el) => headerFooterRels.indexOf(el)));
    if (lastWantedIndex === wanted.length - 1) return relsXml;

    for (let i = wanted.length - 1; i >= 0; i -= 1) {
        root.insertBefore(wanted[i], root.firstChild);
    }

    const serialized = new XMLSerializer().serializeToString(doc);
    if (XML_DECLARATION.test(serialized)) return serialized;

    const declaration = relsXml.match(XML_DECLARATION);
    return declaration ? `${declaration[0]}\n${serialized}` : serialized;
};

const repairDocx = async (file) => {
    const zip = await JSZip.loadAsync(file);

    let changed = false;

    for (const path of Object.keys(zip.files)) {
        if (zip.files[path].dir || !IMPORTED_PART_PATTERN.test(path)) continue;
        const xml = await zip.file(path).async("string");
        const repaired = removeEmptyHyperlinks(xml);
        if (repaired !== xml) {
            zip.file(path, repaired);
            changed = true;
        }
    }

    const documentEntry = zip.file(DOCUMENT_PATH);
    const relsEntry = zip.file(RELS_PATH);
    if (documentEntry && relsEntry) {
        const refIds = getFirstSectionRefIds(await documentEntry.async("string"));
        const relsXml = await relsEntry.async("string");
        const reorderedRelsXml = moveRelationshipsToFront(relsXml, refIds);
        if (reorderedRelsXml !== relsXml) {
            zip.file(RELS_PATH, reorderedRelsXml);
            changed = true;
        }
    }

    if (!changed) return file;


    const blob = await zip.generateAsync({
        type: "blob",
        mimeType: file.type,
        compression: "DEFLATE"
    });
    return new File([blob], file.name, { type: file.type, lastModified: file.lastModified });
};


export const prepareDocxForSuperDoc = async (file) => {
    if (!file) return file;

    try {
        return await repairDocx(file);
    } catch {
        // Unreadable zip, an entry that won't decompress, a re-zip that ran
        // out of memory - all non-fatal. Never reject: the only consumer
        // gates its entire UI on this promise settling.
        return file;
    }
};
